
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { UserProfileData } from '@/types/userProfile';

export const useUserProfileData = (userSlug: string | undefined) => {
  const [profile, setProfile] = useState<UserProfileData | null>(null);
  const [userTeams, setUserTeams] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAllUserTeams = async (userId: string, isInternal: boolean = false) => {
    try {
      console.log(`Fetching comprehensive teams for ${isInternal ? 'internal' : 'supabase'} user:`, userId);
      
      // For internal users, fetch connected_teams from profile first
      let connectedTeamIds: number[] = [];
      if (isInternal) {
        const { data: profileData } = await supabase
          .from('internal_user_profiles')
          .select('connected_teams')
          .eq('user_id', userId)
          .single();
        
        connectedTeamIds = (profileData?.connected_teams as number[]) || [];
        console.log('Internal user connected_teams:', connectedTeamIds);
      }
      
      // Enhanced parallel query execution for better performance
      const [playerTeamsResult, coachTeamsResult, ownedTeamsResult] = await Promise.allSettled([
        // Query 1: Teams where user is a player
        supabase
          .from('team_players')
          .select(`
            position,
            joined_at,
            team:team_id (
              team_id,
              team_name,
              team_description,
              team_logo,
              sport_type,
              location,
              city,
              state_province,
              slug,
              created_at
            )
          `)
          .eq('user_id', userId),

        // Query 2: Teams where user is a coach
        supabase
          .from('team_coaches')
          .select(`
            role,
            added_at,
            team:team_id (
              team_id,
              team_name,
              team_description,
              team_logo,
              sport_type,
              location,
              city,
              state_province,
              slug,
              created_at
            )
          `)
          .eq('coach_id', userId),

        // Query 3: Teams owned by user - use connected_teams for internal users, created_by for Supabase users
        isInternal && connectedTeamIds.length > 0
          ? supabase
              .from('teams')
              .select(`
                team_id,
                team_name,
                team_description,
                team_logo,
                sport_type,
                location,
                city,
                state_province,
                slug,
                created_at
              `)
              .in('team_id', connectedTeamIds)
          : supabase
              .from('teams')
              .select(`
                team_id,
                team_name,
                team_description,
                team_logo,
                sport_type,
                location,
                city,
                state_province,
                slug,
                created_at
              `)
              .eq('created_by', userId)
      ]);

      const teams = [];
      const teamIds = new Set<number>();

      // Process player teams
      if (playerTeamsResult.status === 'fulfilled' && !playerTeamsResult.value.error && playerTeamsResult.value.data) {
        for (const item of playerTeamsResult.value.data) {
          if (item.team) {
            teams.push({
              ...item.team,
              user_role: 'player',
              position: item.position,
              joined_at: item.joined_at,
              connection_type: 'player'
            });
            teamIds.add(item.team.team_id);
          }
        }
      } else if (playerTeamsResult.status === 'rejected') {
        console.warn('Player teams query failed:', playerTeamsResult.reason);
      }

      // Process coach teams
      if (coachTeamsResult.status === 'fulfilled' && !coachTeamsResult.value.error && coachTeamsResult.value.data) {
        for (const item of coachTeamsResult.value.data) {
          if (item.team) {
            const existingTeam = teams.find(t => t.team_id === item.team.team_id);
            if (existingTeam) {
              // User has multiple roles - prioritize coach over player
              existingTeam.user_role = 'coach';
              existingTeam.coaching_role = item.role;
              existingTeam.coach_added_at = item.added_at;
            } else {
              teams.push({
                ...item.team,
                user_role: 'coach',
                coaching_role: item.role,
                joined_at: item.added_at,
                connection_type: 'coach'
              });
              teamIds.add(item.team.team_id);
            }
          }
        }
      } else if (coachTeamsResult.status === 'rejected') {
        console.warn('Coach teams query failed:', coachTeamsResult.reason);
      }

      // Process owned teams
      if (ownedTeamsResult.status === 'fulfilled' && !ownedTeamsResult.value.error && ownedTeamsResult.value.data) {
        for (const team of ownedTeamsResult.value.data) {
          const existingTeam = teams.find(t => t.team_id === team.team_id);
          if (existingTeam) {
            // Owner takes precedence over all other roles
            existingTeam.user_role = 'owner';
            existingTeam.is_owner = true;
            existingTeam.team_created_at = team.created_at;
          } else {
            teams.push({
              ...team,
              user_role: 'owner',
              is_owner: true,
              joined_at: team.created_at,
              connection_type: 'owner'
            });
            teamIds.add(team.team_id);
          }
        }
      } else if (ownedTeamsResult.status === 'rejected') {
        console.warn('Owned teams query failed:', ownedTeamsResult.reason);
      }

      // Efficiently get member counts for all teams in parallel
      if (teamIds.size > 0) {
        const memberCountPromises = Array.from(teamIds).map(async (teamId) => {
          try {
            const { count } = await supabase
              .from('team_players')
              .select('*', { count: 'exact', head: true })
              .eq('team_id', teamId);
            return { teamId, count: count || 0 };
          } catch (error) {
            console.warn(`Failed to get member count for team ${teamId}:`, error);
            return { teamId, count: 0 };
          }
        });

        const memberCounts = await Promise.all(memberCountPromises);
        const memberCountMap = memberCounts.reduce((acc, { teamId, count }) => {
          acc[teamId] = count;
          return acc;
        }, {} as Record<number, number>);

        // Add member counts to teams
        teams.forEach(team => {
          team.member_count = memberCountMap[team.team_id] || 0;
        });
      }

      // Sort teams by role priority and team name
      teams.sort((a, b) => {
        const rolePriority = { owner: 0, coach: 1, support: 2, player: 3 };
        const aPriority = rolePriority[a.user_role as keyof typeof rolePriority] ?? 4;
        const bPriority = rolePriority[b.user_role as keyof typeof rolePriority] ?? 4;
        
        if (aPriority !== bPriority) {
          return aPriority - bPriority;
        }
        return a.team_name.localeCompare(b.team_name);
      });

      console.log(`Found ${teams.length} teams for user:`, teams);
      return teams;

    } catch (error) {
      console.error('Error fetching comprehensive user teams:', error);
      return [];
    }
  };

  useEffect(() => {
    if (!userSlug) {
      setLoading(false);
      return;
    }

    const fetchUserProfile = async () => {
      // Strip @ prefix if present (e.g. /@look -> look)
      const cleanSlug = userSlug.startsWith('@') ? userSlug.slice(1) : userSlug;
      try {
        console.log('Fetching profile for slug:', cleanSlug);
        
        // Try to find user by slug in profiles table (Supabase users)
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .ilike('slug', cleanSlug)
          .maybeSingle();

        if (profileData) {
          console.log('Supabase profile data found:', profileData);
          setProfile({
            ...profileData,
            venue_capacity: profileData.venue_capacity?.toString(),
            hourly_rates: profileData.hourly_rates?.toString()
          });

          // Fetch comprehensive team data for Supabase users
          const allTeams = await fetchAllUserTeams(profileData.id, false);
          console.log('All teams data:', allTeams);
          setUserTeams(allTeams);
          return;
        }

        // If not found in profiles, try to find internal user by username
        console.log('Profile not found in profiles table, checking internal users...');
        
        const { data: internalUser, error: internalUserError } = await supabase
          .from('internal_users')
          .select('id, username, role, age_group, created_at')
          .ilike('username', cleanSlug)
          .maybeSingle();

        if (internalUserError) {
          console.error('Error fetching internal user:', internalUserError);
        }

        if (internalUser) {
          console.log('Internal user found:', internalUser);
          
          // Check for internal user profile
          const { data: internalProfile, error: internalProfileError } = await supabase
            .from('internal_user_profiles')
            .select('*')
            .eq('user_id', internalUser.id)
            .maybeSingle();

          // Convert internal user data to UserProfileData format
          const convertedProfile: UserProfileData = {
            id: internalUser.id,
            username: internalUser.username,
            first_name: internalProfile?.first_name || '',
            last_name: internalProfile?.last_name || '',
            email: '', // Internal users don't have email in the users table
            role: internalUser.role === 'scorekeeper' ? 'support' : internalUser.role,
            age_group: internalUser.age_group,
            slug: internalUser.username, // Use username as slug for internal users
            created_at: internalUser.created_at,
            phone: internalProfile?.phone,
            bio: internalProfile?.bio || internalProfile?.player_bio,
            profile_photo_url: internalProfile?.profile_photo_url,
            isInternal: true,
            // Add other profile fields if they exist
            years_playing: internalProfile?.years_playing,
            years_coaching: internalProfile?.years_coaching,
            position: internalProfile?.position,
            skill_level: internalProfile?.skill_level,
            preferred_sports: internalProfile?.preferred_sports,
            achievements: internalProfile?.achievements,
            short_term_goals: internalProfile?.short_term_goals,
            long_term_goals: internalProfile?.long_term_goals,
            current_sport: internalProfile?.current_sport,
            season_status: internalProfile?.season_status,
            player_bio: internalProfile?.player_bio,
            highlight_video_links: internalProfile?.highlight_video_links,
            height: internalProfile?.height,
            weight: internalProfile?.weight,
            fitness_level: internalProfile?.fitness_level,
            grade_level: internalProfile?.grade_level,
            photo_gallery_links: internalProfile?.photo_gallery_links,
            primary_sport: internalProfile?.primary_sport,
            coach_type: internalProfile?.coach_type,
            coaching_style: internalProfile?.coaching_style,
            coaching_certifications: internalProfile?.coaching_certifications,
            previous_teams: internalProfile?.previous_teams,
            specializations: internalProfile?.specializations,
            availability: internalProfile?.availability,
            show_contact_public: internalProfile?.show_contact_public,
            show_profile_public: internalProfile?.show_profile_public,
            jersey_number: internalProfile?.jersey_number,
            // Venue-specific fields
            venue_name: internalProfile?.venue_name,
            venue_address: internalProfile?.venue_address,
            venue_description: internalProfile?.venue_description,
            venue_type: internalProfile?.venue_type,
            venue_capacity: internalProfile?.venue_capacity?.toString(),
            hourly_rates: internalProfile?.hourly_rates?.toString(),
            operating_hours: internalProfile?.operating_hours,
            available_dates: internalProfile?.available_dates,
            booking_contact_email: internalProfile?.booking_contact_email,
            venue_manager_name: internalProfile?.venue_manager_name,
            venue_manager_contact: internalProfile?.venue_manager_contact,
            locker_rooms_available: internalProfile?.locker_rooms_available,
            parking_available: internalProfile?.parking_available,
            restrooms_available: internalProfile?.restrooms_available,
            concessions_available: internalProfile?.concessions_available,
            show_venue_info_public: internalProfile?.show_venue_info_public,
            show_venue_rates_public: internalProfile?.show_venue_rates_public,
            facilities_available: internalProfile?.facilities_available,
            // Fan-specific fields
            favorite_teams: internalProfile?.favorite_teams as string[] | undefined,
            favorite_sports: internalProfile?.favorite_sports as string[] | undefined,
            fan_since: internalProfile?.fan_since,
            // Parent-specific fields
            children_info: internalProfile?.children_info,
            parenting_sports_involvement: internalProfile?.parenting_sports_involvement,
            // Team Owner-specific fields
            team_history: internalProfile?.team_history,
            team_colors: internalProfile?.team_colors,
            team_motto: internalProfile?.team_motto,
            budget_range: internalProfile?.budget_range,
            sponsorships: internalProfile?.sponsorships,
            venue_selection: internalProfile?.venue_selection,
            facilities_owned: internalProfile?.facilities_owned as string[] | undefined,
            facilities_needs: internalProfile?.facilities_needs,
            team_goals: internalProfile?.team_goals,
            team_preferences: internalProfile?.team_preferences,
            show_team_info_public: internalProfile?.show_team_info_public,
            show_budget_public: internalProfile?.show_budget_public,
            // Creative-specific fields
            creative_role: internalProfile?.creative_role,
            portfolio_link: internalProfile?.portfolio_link,
            creative_specializations: internalProfile?.creative_specializations,
            equipment_used: internalProfile?.equipment_used,
            creative_availability: internalProfile?.creative_availability,
            years_creative_experience: internalProfile?.years_creative_experience,
            creative_certifications: internalProfile?.creative_certifications,
            notable_projects: internalProfile?.notable_projects,
          };

          setProfile(convertedProfile);
          
          // Fetch comprehensive team data for internal users
          const allTeams = await fetchAllUserTeams(internalUser.id, true);
          console.log('Internal user teams data:', allTeams);
          setUserTeams(allTeams);
          return;
        }

        // If neither Supabase user nor internal user found
        console.log('No user found for slug:', cleanSlug);
        setProfile(null);

      } catch (error) {
        console.error('Error in fetchUserProfile:', error);
        setProfile(null);
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, [userSlug]);

  return { profile, userTeams, loading };
};
